import { useState } from 'react';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useMitarbeiter } from '@/hooks/useSupabase';
import { PRUEFUNG_STATUS_OPTIONEN, type PruefungStatus } from '@/lib/akten-pruefung';

interface PruefungDialogProps {
  akteId: string;
  aktuellerStatus: string | null | undefined;
  /** UUID aus akten.pruefung_zugewiesen_an */
  pruefung_zugewiesen_an: string | null | undefined;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function PruefungDialog({
  akteId,
  aktuellerStatus,
  pruefung_zugewiesen_an,
  open,
  onClose,
  onSuccess,
}: PruefungDialogProps) {
  const { data: mitarbeiterData } = useMitarbeiter();
  const mitarbeiter = mitarbeiterData ?? [];

  const [neuerStatus, setNeuerStatus]     = useState<PruefungStatus>(
    (aktuellerStatus as PruefungStatus) || 'keine',
  );
  const [prueferMaId, setPrueferMaId] = useState<string>(pruefung_zugewiesen_an ?? '');
  const [saving, setSaving]               = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/akten-pruefung', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          action:        'setzePruefungStatus',
          akte_id:       akteId,
          neuer_status:  neuerStatus,
          zugewiesen_an: prueferMaId || null,
        }),
      });

      if (!resp.ok) {
        const { error } = await resp.json().catch(() => ({ error: 'Unbekannter Fehler' }));
        toast.error(`QS-Status konnte nicht gesetzt werden: ${error}`);
        return;
      }

      toast.success('QS-Status aktualisiert');
      onSuccess?.();
      onClose();
    } catch {
      toast.error('Netzwerkfehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" aria-label="Prüfung / QS">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-600" />
            Prüfung / Qualitätssicherung
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <label className="text-[11px] text-gray-500 uppercase tracking-wide">QS-Status</label>
            <select
              data-testid="pruefung-status-select"
              value={neuerStatus}
              onChange={(e) => setNeuerStatus(e.target.value as PruefungStatus)}
              className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              {PRUEFUNG_STATUS_OPTIONEN.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-[11px] text-gray-500 uppercase tracking-wide">
              Prüfer zuweisen
            </label>
            <select
              data-testid="pruefung-pruefer-select"
              value={prueferMaId}
              onChange={(e) => setPrueferMaId(e.target.value)}
              className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <option value="">— niemand —</option>
              {mitarbeiter.map((ma) => (
                <option key={ma.id} value={ma.id}>
                  {ma.vorname} {ma.nachname}
                </option>
              ))}
            </select>
            <p className="text-[10px] text-gray-400">
              Prüfer erhält eine Benachrichtigung wenn QS-Status auf „QS erforderlich" gesetzt wird.
            </p>
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            data-testid="pruefung-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Speichern
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
